import { IFramework, IWebServiceManager, HttpMethod, IUserContext } from "@truckdown/framework";
import { ILocationQueryService } from "./ILocationQueryService";
import { ArrayResultModel } from "./Models/ArrayResultModel";
import { BasicLocationInfo } from "./Models/BasicLocationInfo";
import { FindLocationsUsingAccounts } from "./Models/FindLocationsUsingAccounts";
import { FindLocationsUsingArea } from "./Models/FindLocationsUsingArea";
import { FindLocationsUsingPoint } from "./Models/FindLocationsUsingPoint";
import { FindLocationsUsingPreference } from "./Models/FindLocationsUsingPreference";
import { FindLocationsUsingTags } from "./Models/FindLocationsUsingTags";
import { Listing } from "./Models/Listing";
import { ListingPhoneNumber } from "./Models/ListingPhoneNumber";
import { LocationModel } from "./Models/LocationModel";
import { LocationQueryOptions } from "./Models/LocationQueryOptions";

class LocationQueryService implements ILocationQueryService {
    constructor(framework: IFramework) {
        this.manager = framework.getRequiredService<IWebServiceManager>('IWebServiceManager');
    }

    readonly manager: IWebServiceManager;
    private readonly basePath: string = '/locations';

    findBasic(locationId: string): Promise<BasicLocationInfo | null> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/find-basic-location/' + encodeURIComponent(locationId),
            method: HttpMethod.GET
        });

        return this.manager
            .send(req)
            .then((res) => {
                if (res.ok) {
                    return res.json()
                        .then((j) => {
                            return j as BasicLocationInfo;
                        });
                }

                if (res.status == 404) {
                    return null;
                }

                console.log("Error getting basic location info " + locationId, res);
                return null;
            });
    }

    getPhoneNumbers(locationId: string): Promise<ListingPhoneNumber[]> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/get-phone-numbers/' + encodeURIComponent(locationId),
            method: HttpMethod.GET
        });

        return this.manager
            .send(req)
            .then((res) => {
                if (res.ok) {
                    return res.json()
                        .then((j) => {
                            return j as ListingPhoneNumber[];
                        });
                }

                if (res.status == 404) {
                    return [];
                }

                console.log("Error getting basic location info " + locationId, res);
                return [];
            });
    }

    findListing(locationId: string, options?: LocationQueryOptions): Promise<Listing | null> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/find-listing/' + encodeURIComponent(locationId) + this.getQueryString(options),
            method: HttpMethod.POST
        }, options);

        return this.manager
            .send(req)
            .then((res) => {
                if (res.ok) {
                    return res.json()
                        .then((j) => {
                            return j as Listing;
                        });
                }

                if (res.status == 404) {
                    return null;
                }

                console.log("Error getting listing " + locationId, res);
                return null;
            });
    }

    private getQueryString(options?: LocationQueryOptions): string {
        if (!options) {
            return '';
        }

        var vals: string[] = [
            "system=" + encodeURIComponent(options.system),
            "measureUnit=" + encodeURIComponent(options.measureUnit)
        ];

        if (options.includeHoursOfOperation) {
            vals.push("includeHoursOfOperation=true");
        } else {
            vals.push("includeHoursOfOperation=false");
        }
        if (options.includeMarketingData) {
            vals.push("includeMarketingData=true");
        } else {
            vals.push("includeMarketingData=false");
        }
        if (options.includeNotes) {
            vals.push("includeNotes=true");
        } else {
            vals.push("includeNotes=false");
        }
        if (options.includePreference) {
            vals.push("includePreference=true");
        } else {
            vals.push("includePreference=false");
        }
        if (options.includeTransactions) {
            vals.push("includeTransactions=true");
        } else {
            vals.push("includeTransactions=false");
        }

        if (options.preferenceServices && options.preferenceServices.length > 0) {
            for (var i = 0; i < options.preferenceServices.length; i++) {
                vals.push("preferenceServices=" + encodeURIComponent(options.preferenceServices[i]));
            }
        }

        return '?' + vals.join('&');
    }

    findLocation(locationId: string, options?: LocationQueryOptions): Promise<LocationModel | null> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/find-location/' + encodeURIComponent(locationId) + this.getQueryString(options),
            method: HttpMethod.GET
        });

        return this.manager
            .send(req)
            .then((res) => {
                if (res.ok) {
                    return res.json()
                        .then((j) => {
                            return j as LocationModel;
                        });
                }

                if (res.status == 404) {
                    return null;
                }

                console.log("Error getting location " + locationId, res);
                return null;
            });
    }

    private findListingsUsingArray(model: string[], options?: LocationQueryOptions): Promise<ArrayResultModel<Listing>>  {
        var data = {
            value: model
        };
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/find-listings' + this.getQueryString(options),
            method: HttpMethod.POST
        }, data);

        return this.manager
            .get<ArrayResultModel<Listing>>(req);
    }

    findListings(model: string[] | FindLocationsUsingTags
        | FindLocationsUsingPreference
        | FindLocationsUsingAccounts
        | FindLocationsUsingPoint
        | FindLocationsUsingArea, options?: LocationQueryOptions): Promise<ArrayResultModel<Listing>> {

        if (Array.isArray(model)) {
            return this.findListingsUsingArray(model, options);
        }

        var path = this.basePath + '/find-listings';
        if (model.hasOwnProperty('type')) {
            path = path + '/by-preference';
        } else if (model.hasOwnProperty('accountIds')) {
            path = path + '/by-account';
        } else if (model.hasOwnProperty('lat')) {
            path = path + '/by-point';
        } else if (model.hasOwnProperty('polygon')) {
            path = path + '/by-area';
        } else if (model.hasOwnProperty('tags')) {
            path = path + '/by-tags';
        }

        let req = this.manager.createRequest({
            service: this.manager.api,
            path: path,
            method: HttpMethod.POST
        }, model);

        return this.manager
            .get<ArrayResultModel<Listing>>(req);
    }

    private findLocationsUsingArray(model: string[], options?: LocationQueryOptions): Promise<ArrayResultModel<LocationModel>> {
        var data = {
            value: model
        };
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/find-locations' + this.getQueryString(options),
            method: HttpMethod.POST
        }, data);

        return this.manager
            .get<ArrayResultModel<LocationModel>>(req);
    }

    findLocations(model: string[] | FindLocationsUsingTags
        | FindLocationsUsingPreference
        | FindLocationsUsingAccounts
        | FindLocationsUsingPoint
        | FindLocationsUsingArea, options?: LocationQueryOptions): Promise<ArrayResultModel<LocationModel>> {

        if (Array.isArray(model)) {
            return this.findLocationsUsingArray(model, options);
        }

        var path = this.basePath + '/find-locations';

        if (model.hasOwnProperty('type')) {
            path = path + '/by-preference';
        } else if (model.hasOwnProperty('accountIds')) {
            path = path + '/by-account';
        } else if (model.hasOwnProperty('lat')) {
            path = path + '/by-point';
        } else if (model.hasOwnProperty('polygon')) {
            path = path + '/by-area';
        } else if (model.hasOwnProperty('tags')) {
            path = path + '/by-tags';
        }

        let req = this.manager.createRequest({
            service: this.manager.api,
            path: path,
            method: HttpMethod.POST
        }, model);

        return this.manager
            .get<ArrayResultModel<LocationModel>>(req);
    }
}

export default (framework: IFramework) => {
    framework.registerTransient<ILocationQueryService>('ILocationQueryService', (frm) => {
        return new LocationQueryService(frm);
    });
}